import React from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridRowParams } from '@mui/x-data-grid';
import { getTenants, getUsersWithHr } from '../services/apiService.ts';

type SearchEntity = 'all' | 'users' | 'tenants' | 'routes';

type RouteResult = {
  id: string;
  label: string;
  path: string;
  description: string;
};

const ROUTE_CATALOG: RouteResult[] = [
  { id: '/dashboard', label: 'Overview', path: '/dashboard', description: 'Global health and shared totals.' },
  { id: '/dashboard/tenants', label: 'Tenants', path: '/dashboard/tenants', description: 'Tenant catalog and billing readiness.' },
  { id: '/dashboard/locations', label: 'Locations', path: '/dashboard/locations', description: 'Focused tenant locations and drilldowns.' },
  { id: '/dashboard/spaces', label: 'Spaces', path: '/dashboard/spaces', description: 'Location floor segments and capacity.' },
  { id: '/dashboard/tables', label: 'Tables', path: '/dashboard/tables', description: 'Seat inventory for the selected space.' },
  { id: '/dashboard/reservations', label: 'Reservations', path: '/dashboard/reservations', description: 'Focused tenant reservation list.' },
  { id: '/dashboard/users', label: 'Users', path: '/dashboard/users', description: 'Internal and tenant user directory.' },
  { id: '/dashboard/access-directory', label: 'Access Directory', path: '/dashboard/access-directory', description: 'Menus, profiles and assignments.' },
  { id: '/dashboard/location-team', label: 'Location Team', path: '/dashboard/location-team', description: 'Location-based access view.' },
  { id: '/dashboard/permission-profiles', label: 'Permission Profiles', path: '/dashboard/permission-profiles', description: 'Support menu and profile catalog.' },
  { id: '/dashboard/support-actions', label: 'Support Actions', path: '/dashboard/support-actions', description: 'Operational audit trail for interventions.' },
  { id: '/dashboard/logs', label: 'Logs', path: '/dashboard/logs', description: 'Live and historical shared logs.' },
  { id: '/dashboard/reports', label: 'Reports', path: '/dashboard/reports', description: 'Shared reporting exports.' },
  { id: '/dashboard/inventory-overview', label: 'Inventory', path: '/dashboard/inventory-overview', description: 'Inventory control overview.' },
  { id: '/dashboard/hotels', label: 'Hotels', path: '/dashboard/hotels', description: 'Hotel catalog and setup.' },
  { id: '/dashboard/hotel-rooms', label: 'Hotel Rooms', path: '/dashboard/hotel-rooms', description: 'Room types for the selected hotel.' },
  { id: '/dashboard/hotel-housekeeping', label: 'Housekeeping', path: '/dashboard/hotel-housekeeping', description: 'Housekeeping task board.' },
];

const routeColumns: GridColDef[] = [
  { field: 'label', headerName: 'Route', flex: 1, minWidth: 180 },
  { field: 'path', headerName: 'Path', flex: 1, minWidth: 220 },
  { field: 'description', headerName: 'Description', flex: 1.4, minWidth: 260 },
];

const tenantColumns: GridColDef[] = [
  { field: 'name', headerName: 'Tenant', flex: 1, minWidth: 180 },
  { field: 'city', headerName: 'City', width: 140 },
  { field: 'status', headerName: 'Status', width: 120 },
  {
    field: 'enabledModules',
    headerName: 'Modules',
    flex: 1,
    minWidth: 200,
    valueGetter: (params) =>
      Array.isArray(params.row.enabledModules)
        ? params.row.enabledModules.join(', ')
        : '',
  },
];

const userColumns: GridColDef[] = [
  { field: 'fullName', headerName: 'User', flex: 1, minWidth: 180 },
  { field: 'email', headerName: 'Email', flex: 1, minWidth: 220 },
  {
    field: 'roles',
    headerName: 'Roles',
    flex: 1,
    minWidth: 180,
    valueGetter: (params) =>
      Array.isArray(params.row.roles) ? params.row.roles.join(', ') : '',
  },
  {
    field: 'jobTitle',
    headerName: 'Job title',
    flex: 1,
    minWidth: 180,
    valueGetter: (params) => params.row.hrProfile?.jobTitle || 'No HR profile',
  },
];

const SearchPage: React.FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const entity = (params.get('entity') || 'all') as SearchEntity;
  const q = (params.get('q') || '').trim();
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [routeResults, setRouteResults] = React.useState<RouteResult[]>([]);
  const [tenantResults, setTenantResults] = React.useState<any[]>([]);
  const [userResults, setUserResults] = React.useState<any[]>([]);

  React.useEffect(() => {
    let active = true;

    const loadResults = async () => {
      if (!q) {
        if (!active) return;
        setRouteResults([]);
        setTenantResults([]);
        setUserResults([]);
        setError(null);
        return;
      }

      setLoading(true);
      setError(null);

      const normalizedQuery = q.toLowerCase();
      const nextRouteResults = (entity === 'all' || entity === 'routes')
        ? ROUTE_CATALOG.filter((route) =>
            `${route.label} ${route.path} ${route.description}`
              .toLowerCase()
              .includes(normalizedQuery),
          ).slice(0, 8)
        : [];

      const tenantPromise = entity === 'all' || entity === 'tenants'
        ? getTenants(0, q)
        : Promise.resolve(null);
      const userPromise = entity === 'all' || entity === 'users'
        ? getUsersWithHr({ search: q, page: 0, size: 8, sort: 'lastName,asc' })
        : Promise.resolve(null);

      const [tenantResponse, userResponse] = await Promise.allSettled([tenantPromise, userPromise]);

      if (!active) {
        return;
      }

      setRouteResults(nextRouteResults);

      if (tenantResponse.status === 'fulfilled' && tenantResponse.value) {
        const tenants = tenantResponse.value.content ?? tenantResponse.value;
        setTenantResults(Array.isArray(tenants) ? tenants : []);
      } else {
        setTenantResults([]);
      }

      if (userResponse.status === 'fulfilled' && userResponse.value) {
        const users = userResponse.value.content ?? userResponse.value;
        const normalizedUsers = Array.isArray(users)
          ? users.map((user: any) => ({
              ...user,
              userId: user.userId ?? user.user_id,
              firstName: user.firstName ?? user.first_name,
              lastName: user.lastName ?? user.last_name,
              hrProfile: user.hrProfile ?? user.hr_profile ?? null,
              fullName: `${user.firstName ?? user.first_name ?? ''} ${user.lastName ?? user.last_name ?? ''}`.trim(),
            }))
          : [];
        setUserResults(normalizedUsers);
      } else {
        setUserResults([]);
      }

      const tenantFailed = tenantResponse.status === 'rejected' && (entity === 'all' || entity === 'tenants');
      const userFailed = userResponse.status === 'rejected' && (entity === 'all' || entity === 'users');
      if (tenantFailed && userFailed) {
        setError('Search is temporarily unavailable for users and tenants.');
      } else if (tenantFailed) {
        setError('Tenant search is temporarily unavailable.');
      } else if (userFailed) {
        setError('User search is temporarily unavailable.');
      }

      setLoading(false);
    };

    void loadResults();

    return () => {
      active = false;
    };
  }, [entity, q]);

  const hasResults = routeResults.length > 0 || tenantResults.length > 0 || userResults.length > 0;

  const renderSection = (
    title: string,
    rows: any[],
    columns: GridColDef[],
    getRowId: (row: any) => string | number,
    onRowClick: (params: GridRowParams) => void,
  ) => {
    if (!rows.length) {
      return null;
    }

    return (
      <Box>
        <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
          {title}
        </Typography>
        <DataGrid
          autoHeight
          hideFooter
          rows={rows}
          columns={columns}
          getRowId={getRowId}
          onRowClick={onRowClick}
          pageSizeOptions={[5]}
          disableRowSelectionOnClick
        />
      </Box>
    );
  };

  return (
    <Box sx={{ p: 2 }}>
      <Stack spacing={1.5}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Search workspace
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {q
              ? `Results for "${q}" across users, tenants and routes.`
              : 'Search by user name, email, tenant name or route label.'}
          </Typography>
        </Box>

        {loading ? <CircularProgress /> : null}

        {error ? (
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        ) : null}

        {!loading && !hasResults && q ? (
          <Typography variant="body2" color="text.secondary">
            No results for "{q}". Try a tenant name, user email or route label.
          </Typography>
        ) : null}

        {renderSection(
          'Routes',
          routeResults,
          routeColumns,
          (row) => row.id,
          (params) => navigate(String(params.row.path)),
        )}

        {renderSection(
          'Tenants',
          tenantResults,
          tenantColumns,
          (row) => row.id,
          (params) => navigate(`/dashboard/tenants/${params.row.id}`),
        )}

        {renderSection(
          'Users',
          userResults,
          userColumns,
          (row) => row.userId,
          (params) => navigate(`/dashboard/users?search=${encodeURIComponent(params.row.email || params.row.fullName || q)}`),
        )}
      </Stack>
    </Box>
  );
};

export default SearchPage;
